// src/pages/user/ProjectDetails.jsx
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getProject } from '../../utils/api';
import Loading from '../../components/common/Loading';
import { 
  FaCalendar, 
  FaMapMarker, 
  FaBuilding, 
  FaArrowLeft,
  FaCheck,
  FaIndustry,
  FaTasks,
  FaLightbulb,
  FaChartLine
} from 'react-icons/fa';

const ProjectDetails = () => {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImage, setActiveImage] = useState(0);

  useEffect(() => {
    loadProject();
  }, [id]);

  const loadProject = async () => {
    try {
      setLoading(true);
      const data = await getProject(id);
      setProject(data);
    } catch (error) {
      console.error('Error loading project:', error);
    } finally {
      setLoading(false);
    }
  };

  // Format dates for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  if (loading) {
    return <Loading fullScreen message="Loading project details..." />;
  }

  if (!project) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Project Not Found</h2>
          <Link 
            to="/projects" 
            className="text-blue-600 hover:text-blue-800 font-medium"
          >
            ← Back to Projects
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Back Button */}
      <div className="container-custom py-6">
        <Link 
          to="/projects" 
          className="inline-flex items-center text-gray-600 hover:text-gray-800"
        >
          <FaArrowLeft className="h-4 w-4 mr-2" />
          Back to Projects
        </Link>
      </div>

      {/* Hero Section with Image */}
      <div className="relative h-96 overflow-hidden bg-gradient-to-r from-blue-900 to-gray-900">
        {project.images && project.images[0] ? (
          <img 
            src={project.images[0]} 
            alt={project.title}
            className="w-full h-full object-cover opacity-30"
          />
        ) : null}
        <div className="absolute inset-0 flex items-center">
          <div className="container-custom">
            <div className="max-w-3xl">
              <div className="flex items-center space-x-4 mb-4">
                <span className={`inline-block px-4 py-2 text-sm font-semibold rounded-full ${
                  project.status === 'completed' ? 'bg-green-100 text-green-800' :
                  project.status === 'ongoing' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-blue-100 text-blue-800'
                }`}>
                  {project.status}
                </span>
                {project.category && project.category !== 'General' && (
                  <span className="inline-block px-4 py-2 text-sm bg-white bg-opacity-20 text-white rounded-full">
                    {project.category}
                  </span>
                )}
              </div>
              <h1 className="text-5xl font-bold text-white mb-6">{project.title}</h1>
              <p className="text-xl text-blue-100">{project.description}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Project Details */}
      <div className="container-custom py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Project Overview */}
            <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
                <FaTasks className="h-6 w-6 mr-3 text-blue-600" />
                Project Overview
              </h2>
              <p className="text-gray-600 mb-6">{project.description}</p>
              
              {/* Key Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                {project.client_name && (
                  <div className="flex items-center">
                    <div className="p-3 bg-blue-100 rounded-lg mr-4">
                      <FaBuilding className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Client</p>
                      <p className="font-semibold text-gray-800">{project.client_name}</p>
                    </div>
                  </div>
                )}
                
                {project.location && (
                  <div className="flex items-center">
                    <div className="p-3 bg-red-100 rounded-lg mr-4">
                      <FaMapMarker className="h-6 w-6 text-red-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Location</p>
                      <p className="font-semibold text-gray-800">{project.location}</p>
                    </div>
                  </div>
                )}
                
                {project.start_date && (
                  <div className="flex items-center">
                    <div className="p-3 bg-green-100 rounded-lg mr-4">
                      <FaCalendar className="h-6 w-6 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Start Date</p>
                      <p className="font-semibold text-gray-800">{formatDate(project.start_date)}</p>
                    </div>
                  </div>
                )}
                
                {project.end_date && (
                  <div className="flex items-center">
                    <div className="p-3 bg-purple-100 rounded-lg mr-4">
                      <FaCalendar className="h-6 w-6 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Completion Date</p>
                      <p className="font-semibold text-gray-800">{formatDate(project.end_date)}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Project Details Sections */}
              {project.details && (
                <>
                  {/* Project Scope */}
                  {project.details.scope && (
                    <div className="mb-8">
                      <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                        <FaIndustry className="h-5 w-5 mr-2 text-blue-600" />
                        Project Scope
                      </h3>
                      <p className="text-gray-600">{project.details.scope}</p>
                    </div>
                  )}

                  {/* Challenges */}
                  {project.details.challenges && (
                    <div className="mb-8">
                      <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                        <FaTasks className="h-5 w-5 mr-2 text-red-600" />
                        Challenges
                      </h3>
                      <p className="text-gray-600">{project.details.challenges}</p>
                    </div>
                  )}

                  {/* Solutions */}
                  {project.details.solutions && (
                    <div className="mb-8">
                      <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                        <FaLightbulb className="h-5 w-5 mr-2 text-yellow-600" />
                        Solutions Implemented
                      </h3>
                      <p className="text-gray-600">{project.details.solutions}</p>
                    </div>
                  )}

                  {/* Results */}
                  {project.details.results && (
                    <div className="mb-8">
                      <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                        <FaChartLine className="h-5 w-5 mr-2 text-green-600" />
                        Results & Achievements
                      </h3>
                      <p className="text-gray-600">{project.details.results}</p>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Image Gallery */}
            {project.images && project.images.length > 0 && (
              <div className="bg-white rounded-xl shadow-lg p-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-6">Project Gallery</h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {project.images.map((image, index) => (
                    <button
                      key={index}
                      onClick={() => setActiveImage(index)}
                      className={`rounded-lg overflow-hidden focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        activeImage === index ? 'ring-2 ring-blue-500' : ''
                      }`}
                    >
                      <img 
                        src={image} 
                        alt={`${project.title} - Image ${index + 1}`}
                        className="w-full h-48 object-cover hover:scale-105 transition-transform duration-300"
                      />
                    </button>
                  ))}
                </div>
                
                {/* Large Image Viewer */}
                {project.images[activeImage] && (
                  <div className="mt-6">
                    <img 
                      src={project.images[activeImage]} 
                      alt={`${project.title} - Main View`}
                      className="w-full h-auto rounded-lg shadow-lg"
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-8">
            {/* Project Status Card */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-lg font-bold text-gray-800 mb-4">Project Status</h3>
              <div className="flex items-center justify-between mb-4">
                <span className="text-gray-600">Completion</span>
                <span className={`font-semibold ${
                  project.status === 'completed' ? 'text-green-600' :
                  project.status === 'ongoing' ? 'text-yellow-600' :
                  'text-blue-600'
                }`}>
                  {project.status === 'completed' ? '100%' : 
                   project.status === 'ongoing' ? 'In Progress' : 
                   'Planning'}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div className={`h-2 rounded-full ${
                  project.status === 'completed' ? 'bg-green-600 w-full' :
                  project.status === 'ongoing' ? 'bg-yellow-600 w-2/3' :
                  'bg-blue-600 w-1/3'
                }`}></div>
              </div>
              <div className="mt-4 text-sm text-gray-500">
                {project.status === 'completed' ? 'Project successfully completed' :
                 project.status === 'ongoing' ? 'Currently in progress' :
                 'In planning phase'}
              </div>
            </div>

            {/* Key Highlights */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-lg font-bold text-gray-800 mb-4">Key Highlights</h3>
              <ul className="space-y-3">
                <li className="flex items-center">
                  <FaCheck className="h-5 w-5 text-green-500 mr-3" />
                  <span>Expert engineering solutions</span>
                </li>
                <li className="flex items-center">
                  <FaCheck className="h-5 w-5 text-green-500 mr-3" />
                  <span>Timely project delivery</span>
                </li>
                <li className="flex items-center">
                  <FaCheck className="h-5 w-5 text-green-500 mr-3" />
                  <span>Quality assurance guaranteed</span>
                </li>
                <li className="flex items-center">
                  <FaCheck className="h-5 w-5 text-green-500 mr-3" />
                  <span>Client satisfaction focused</span>
                </li>
              </ul>
            </div>

            {/* Contact CTA */}
            <div className="bg-blue-600 rounded-xl shadow-lg p-6">
              <h3 className="text-lg font-bold text-white mb-4">Start Your Project</h3>
              <p className="text-blue-100 mb-6">
                Interested in our services? Let's discuss your project requirements.
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center w-full bg-white text-blue-600 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors duration-200"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Related Projects (if available) */}
      {project.category && project.category !== 'General' && (
        <div className="bg-gray-100 py-16">
          <div className="container-custom">
            <h2 className="text-3xl font-bold text-gray-800 mb-8 text-center">
              Similar Projects
            </h2>
            <p className="text-gray-600 text-center mb-12 max-w-2xl mx-auto">
              Explore other projects in the same category
            </p>
            <Link
              to="/projects"
              className="block mx-auto w-fit px-8 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors duration-200 text-center"
            >
              View All Projects
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectDetails;